import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearch } from '@tanstack/react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import {
  esCamion,
  formatMoney,
  formatQuantity,
  hoy,
  parseQuantity,
  costOf,
  newId,
  type Centavos,
  type Milli,
} from '@abastece/shared';
import type { Existencia, Producto } from '@abastece/core';
import { db } from '../db/dexie.js';
import { emitirYVer } from '../lib/emitir.js';
import { coincideProducto } from '../lib/catalogo.js';
import { existenciaPorLugar, productosDelCodigo } from '../lib/inventario.js';
import { useLectorUsb } from '../lib/lector-usb.js';
import { usePuede } from '../lib/sesion.js';
import { PreguntaCodigo } from '../ui/BuscadorProducto.js';
import { Campo, Selector } from '../ui/Campo.js';
import { EnsenarCodigo } from '../ui/EnsenarCodigo.js';
import { BotonEscanear } from '../ui/Escaner.js';
import { EscanerProductos } from '../ui/EscanerProductos.js';
import { FormularioProducto } from '../ui/FormularioProducto.js';
import { Modal } from '../ui/Modal.js';
import { Pagina, Vacio } from '../ui/Pagina.js';
import { SugerenciaAbierta } from '../ui/SugerenciaAbierta.js';
import { Chip, Ico } from '../ui/Iconos.js';
import { localeActual, useT, type Clave } from '../i18n/index.js';
import { etiquetaLugar, etiquetaUnidad } from '../i18n/dominio.js';
import { nombreProducto, useNombreProducto } from '../i18n/productos.js';
import { Miniatura } from '../ui/Miniatura.js';

const MOTIVOS = [
  { valor: 'merma', etiqueta: 'salidaInv.merma' },
  { valor: 'vencido', etiqueta: 'salidaInv.vencido' },
  { valor: 'consumo_interno', etiqueta: 'salidaInv.consumo_interno' },
  { valor: 'robo', etiqueta: 'salidaInv.robo' },
] as const satisfies readonly { valor: string; etiqueta: Clave }[];

/**
 * A code scanned here, being answered in a sheet: the products it is (one, or both of two it was
 * given to by mistake), or a code nobody has, then being given to a product or made one.
 */
type Escaneo =
  | { tipo: 'hallado'; productos: readonly Producto[] }
  | { tipo: 'desconocido' | 'ensenar' | 'crear'; codigo: string };

export function Inventario() {
  const t = useT();
  const nombre = useNombreProducto();
  const productos = useLiveQuery(() => db.productos.toArray(), [], []);
  const existencias = useLiveQuery(() => db.existencias.toArray(), [], []);
  const puedeAjustar = usePuede('ajustar_inventario');
  /**
   * What the stock is worth, row by row and in all, is the books' average cost: the owner's to
   * see, not a cashier's, who counts and moves it as well since 2026-10-09.
   */
  const verCostos = usePuede('ver_costos');
  const { negativos: soloNegativos } = useSearch({ from: '/inventario' });

  const [consulta, setConsulta] = useState('');
  const [sacando, setSacando] = useState<{ producto: Producto; ubicacion: string } | null>(null);
  const [contando, setContando] = useState<{ producto: Producto; ubicacion: string } | null>(null);
  const unidades = useLiveQuery(() => db.unidades.toArray(), [], []);
  const puedeEditar = usePuede('editar_productos');
  /** The search box, where whatever a USB scanner types on this screen is read (`useLectorUsb`). */
  const caja = useRef<HTMLInputElement>(null);
  const [escaneando, setEscaneando] = useState(false);
  /** What a scan found, in its sheet; `camara` when the camera read it, which «Seguir escaneando» opens again. */
  const [escaneo, setEscaneo] = useState<{ que: Escaneo; camara: boolean } | null>(null);
  /** Where the cursor was when the scan came, for it to go back to once the sheet is let go. */
  const cursorAntes = useRef<HTMLElement | null>(null);

  /*
   * The shop's stock, the one place there is (the owner, 2026-10-05: «we have just one location»).
   * Anything somewhere else, an old truck's or what a phone not yet updated put in the storeroom,
   * is listed too, under its place, until «Traslado» brings it to the shop: never hidden.
   */
  const fueraDeLaTienda = existencias.some((e) => e.ubicacion !== 'tienda' && e.cantidad !== 0);
  const filas = useMemo(() => {
    const porId = new Map(productos.map((p) => [p.id, p]));
    return existencias
      .filter((e) => e.ubicacion === 'tienda' || e.cantidad !== 0)
      .filter((e) => !soloNegativos || (e.cantidad < 0 && !esCamion(e.ubicacion)))
      .map((e) => ({ existencia: e, producto: porId.get(e.productoId) }))
      .filter((f): f is { existencia: typeof f.existencia; producto: Producto } => Boolean(f.producto))
      .filter((f) => !consulta.trim() || coincideProducto(f.producto, consulta))
      .sort((a, b) => nombre(a.producto).localeCompare(nombre(b.producto), localeActual()));
  }, [existencias, productos, consulta, nombre, soloNegativos]);

  /**
   * The whole stock, whatever the list is showing: what the title says it is worth, and how many are
   * below zero. Added up over the rows shown, the view of the negatives said «-C$ 45.40 en
   * existencia», and a search with no negative in it took «Ver todo» away (2026-10-13).
   */
  const { valorTotal, negativos } = useMemo(() => {
    const hay = new Set(productos.map((p) => p.id));
    const todas = existencias.filter((e) => hay.has(e.productoId) && (e.ubicacion === 'tienda' || e.cantidad !== 0));
    return {
      valorTotal: todas.reduce((a, e) => a + costOf(e.costoPromedio, e.cantidad), 0) as Centavos,
      negativos: todas.filter((e) => e.cantidad < 0 && !esCamion(e.ubicacion)).length,
    };
  }, [existencias, productos]);

  const enfocado = () => (document.activeElement instanceof HTMLElement ? document.activeElement : null);

  /**
   * What a USB scanner read, wherever the cursor was, as on the other screens that look products up
   * (the owner, 2026-10-04): what there is of its product, and where, in a sheet that has to be let
   * go; a code nobody has is asked about there, with «Crear producto» among the ways out. Whatever
   * was typed into the search, which goes on filtering the list by name, is left as it was.
   */
  function leerCodigo(codigo: string) {
    cursorAntes.current = enfocado();
    const hallados = productosDelCodigo(productos, unidades, codigo);
    setEscaneo({
      que: hallados.length ? { tipo: 'hallado', productos: hallados } : { tipo: 'desconocido', codigo },
      camara: false,
    });
  }
  useLectorUsb(caja, leerCodigo);

  /** The next sheet for the same scan: the code given to a product, or made one, shows that product. */
  const pasarA = (que: Escaneo) => setEscaneo((e) => ({ que, camara: e?.camara ?? false }));
  const hallado = (p: Producto) => pasarA({ tipo: 'hallado', productos: [p] });

  /** The product just made in the sheet, as found: in the catalogue now, and nowhere yet. */
  async function creado({ productoId }: { productoId: string; unidadVentaId: string }) {
    const producto = await db.productos.get(productoId);
    if (producto) hallado(producto);
    else soltar();
  }

  /** The sheet let go: the cursor goes back where it was when the scan came. */
  function soltar() {
    setEscaneo(null);
    const antes = cursorAntes.current;
    cursorAntes.current = null;
    if (antes?.isConnected) antes.focus();
  }

  /** «Seguir escaneando»: the camera again when it was what read the code; otherwise the cursor back. */
  function seguir() {
    if (!escaneo?.camara) return soltar();
    setEscaneo(null);
    setEscaneando(true);
  }
  const que = escaneo?.que;

  return (
    <Pagina
      titulo={t('inv.titulo')}
      subtitulo={verCostos ? t('inv.enExistencia', { monto: formatMoney(valorTotal) }) : undefined}
      acciones={
        puedeAjustar && (
          <Link to="/inventario/conteo" className="btn-primario gap-1.5 px-4 text-sm">
            <Ico icono="contar" className="size-4" />
            {t('inv.contar')}
          </Link>
        )
      }
    >
      {/*
        Vencimientos is the tab beside this one now; these are things to do with the stock. «Traslado»
        only while something is outside the shop, to bring it in.
      */}
      <nav className={`mb-4 grid gap-2 ${fueraDeLaTienda ? 'grid-cols-2' : 'grid-cols-1'}`}>
        <Link to="/inventario/reempaque" className="btn-secundario h-12 min-h-0 gap-1.5 px-3 text-sm">
          <Ico icono="inventario" className="size-4 shrink-0" />
          <span className="truncate">{t('inv.reempaque')}</span>
        </Link>
        {fueraDeLaTienda && (
          <Link to="/inventario/traslado" className="btn-secundario h-12 min-h-0 gap-1.5 px-3 text-sm">
            <Ico icono="reparto" className="size-4 shrink-0" />
            <span className="truncate">{t('inv.traslado')}</span>
          </Link>
        )}
      </nav>

      {/* The search and its camera, as on the other screens that look products up. */}
      <div className="mb-4 flex flex-col gap-3 sm:flex-row">
        <div className="flex min-w-0 flex-1 gap-2">
          <input
            ref={caja}
            className="campo min-w-0 flex-1"
            placeholder={t('inv.buscar')}
            value={consulta}
            onChange={(e) => setConsulta(e.target.value)}
            autoComplete="off"
            inputMode="search"
          />
          <BotonEscanear
            onClick={() => {
              cursorAntes.current = enfocado();
              setEscaneando(true);
            }}
          />
        </div>
      </div>

      {negativos > 0 && (
        <p className="mb-4 rounded-xl bg-amber-500/10 px-4 py-3 text-sm text-amber-200">
          {t('inv.negativos', { n: negativos })}{' '}
          {/* What the screen shows, not a screen of its own: no step for «Volver» to undo (`lib/volver.ts`). */}
          {soloNegativos ? (
            <Link to="/inventario" replace className="font-medium underline underline-offset-2 hover:text-white hover:decoration-2" data-sin-icono="pestana">
              {t('inv.verTodo')}
            </Link>
          ) : (
            <Link
              to="/inventario"
              search={{ negativos: true }}
              replace
              className="font-medium underline underline-offset-2 hover:text-white hover:decoration-2"
              data-sin-icono="pestana"
            >
              {t('inv.verNegativos')}
            </Link>
          )}
        </p>
      )}

      {/* The last of the negatives counted from the list: said, with the way back to the rest, not an empty list. */}
      {filas.length === 0 && soloNegativos && negativos === 0 ? (
        <p className="rounded-xl bg-emerald-500/10 px-4 py-3 text-sm text-emerald-200" data-sin-negativos>
          {t('inv.sinNegativos')}{' '}
          <Link to="/inventario" replace className="font-medium underline underline-offset-2 hover:text-white hover:decoration-2" data-sin-icono="pestana">
            {t('inv.verTodo')}
          </Link>
        </p>
      ) : filas.length === 0 ? (
        <Vacio mensaje={t('inv.sinExistencias')} icono="inventario" />
      ) : (
        <ul className="flex flex-col gap-2" data-guia="inventario-lista">
          {filas.map((f) => {
            const valor = costOf(f.existencia.costoPromedio, f.existencia.cantidad);
            return (
              /*
                Wraps rather than squeezes: on a narrow phone the quantity and «Sacar» drop under
                the name instead of leaving the name one letter wide beside them.
              */
              <li
                key={f.existencia.id}
                className="tarjeta flex flex-wrap items-center gap-x-3 gap-y-2 py-3 pl-3"
              >
                <Miniatura producto={f.producto} tamano="sm" />
                <div className="min-w-36 flex-1">
                  <p className="truncate font-medium">{nombre(f.producto)}</p>
                  <p className="mt-0.5 text-xs text-slate-500 empty:hidden">
                    {/* The place only where it is not the shop: what is left somewhere else says where. */}
                    {f.existencia.ubicacion !== 'tienda' && etiquetaLugar(f.existencia.ubicacion)}
                    {f.existencia.ubicacion !== 'tienda' && verCostos && ' · '}
                    {verCostos && formatMoney(valor)}
                  </p>
                </div>
                {/* Below zero and narrow, the two buttons go under the quantity together rather than past the card. */}
                <div className="ml-auto flex flex-wrap items-center justify-end gap-x-3 gap-y-2">
                  <span
                    className={`tabular font-semibold ${
                      f.existencia.cantidad < 0 ? 'text-rose-400' : ''
                    }`}
                  >
                    {formatQuantity(f.existencia.cantidad)}{' '}
                    <span className="text-xs font-normal text-slate-500">
                      {etiquetaUnidad(f.producto.unidadBase, f.producto.unidadBase)}
                    </span>
                  </span>
                  {puedeAjustar && (
                    <div className="flex items-center gap-2">
                      {/* A truck's stock is counted at its route's settlement. */}
                      {f.existencia.cantidad < 0 && !esCamion(f.existencia.ubicacion) && (
                        <button
                          type="button"
                          className="inline-flex items-center gap-1 rounded-lg bg-slate-800 px-2.5 py-1.5 text-xs text-slate-300 transition hover:bg-slate-700 hover:text-slate-100"
                          data-a-cero
                          onClick={() => setContando({ producto: f.producto, ubicacion: f.existencia.ubicacion })}
                        >
                          <Ico icono="contar" className="size-3.5" />
                          {t('inv.aCero')}
                        </button>
                      )}
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 rounded-lg bg-slate-800 px-2.5 py-1.5 text-xs text-slate-300 transition hover:bg-slate-700 hover:text-slate-100"
                        onClick={() => setSacando({ producto: f.producto, ubicacion: f.existencia.ubicacion })}
                      >
                        <Ico icono="merma" className="size-3.5" />
                        {t('inv.sacar')}
                      </button>
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {sacando && (
        <SalidaSinVenta
          producto={sacando.producto}
          ubicacion={sacando.ubicacion}
          onCerrar={() => setSacando(null)}
        />
      )}

      {contando && (
        <ContarEnCero
          producto={contando.producto}
          ubicacion={contando.ubicacion}
          sistema={
            existencias.find((e) => e.productoId === contando.producto.id && e.ubicacion === contando.ubicacion)?.cantidad ??
            (0 as Milli)
          }
          onCerrar={() => setContando(null)}
        />
      )}

      {/*
        The camera reads one code and closes for its product's sheet, whose «Seguir escaneando»
        opens it again. A code nobody has is asked about over the picture, as everywhere, with
        «Crear producto» among the ways out.
      */}
      {escaneando && (
        <EscanerProductos
          titulo={t('productos.escanearTitulo')}
          onProducto={(p) => setEscaneo({ que: { tipo: 'hallado', productos: [p] }, camara: true })}
          onCerrar={() => setEscaneando(false)}
          sugerir={(codigo, listo) => <SugerenciaAbierta codigo={codigo} onElegido={listo} conPista={puedeEditar} />}
          desconocido={
            puedeEditar
              ? (codigo, cerrar) => (
                  <button
                    type="button"
                    className="btn-secundario"
                    onClick={() => {
                      cerrar();
                      setEscaneo({ que: { tipo: 'crear', codigo }, camara: true });
                    }}
                  >
                    <Ico icono="nuevo" />
                    {t('productos.crearConCodigo')}
                  </button>
                )
              : undefined
          }
        />
      )}

      {que?.tipo === 'hallado' && (
        <Modal titulo={t('inv.escaneado')} onCerrar={soltar}>
          <ProductoEscaneado productos={que.productos} existencias={existencias} onSeguir={seguir} />
        </Modal>
      )}

      {/* A code scanned that nobody has: the other screens' «Código desconocido», with a new product among its ways out. */}
      {que?.tipo === 'desconocido' && (
        <Modal titulo={t('buscador.preguntaTitulo')} onCerrar={soltar}>
          <PreguntaCodigo
            codigo={que.codigo}
            puedeEnsenar={puedeEditar}
            conPista={puedeEditar}
            onEnsenar={() => pasarA({ tipo: 'ensenar', codigo: que.codigo })}
            onSeguir={soltar}
            onElegido={hallado}
          >
            {puedeEditar && (
              <button type="button" className="btn-secundario" onClick={() => pasarA({ tipo: 'crear', codigo: que.codigo })}>
                <Ico icono="nuevo" />
                {t('productos.crearConCodigo')}
              </button>
            )}
          </PreguntaCodigo>
        </Modal>
      )}

      {que?.tipo === 'ensenar' && (
        <Modal titulo={t('escaner.cualProducto')} onCerrar={soltar}>
          <p className="mb-4 text-sm text-slate-400">{t('escaner.cualProductoAyuda', { codigo: que.codigo })}</p>
          <EnsenarCodigo codigo={que.codigo} onListo={hallado} />
        </Modal>
      )}

      {/* Made here, the code already in it; once saved, its sheet says it has nothing anywhere yet. */}
      {que?.tipo === 'crear' && (
        <Modal titulo={t('producto.nuevo')} onCerrar={soltar}>
          <p className="mb-4 text-sm text-slate-400">{t('inv.productoNuevoAyuda')}</p>
          <FormularioProducto enHoja codigo={que.codigo} onCreado={(c) => void creado(c)} />
        </Modal>
      )}
    </Pagina>
  );
}

/**
 * A product scanned here: what there is of it, place by place and in its own unit, which is what
 * the scan was for; then its page, or back to scanning. The cursor goes into the sheet, out of the
 * field it was in, so that the next scan's Enter presses none of this. A code on two products (the
 * same goods registered twice) shows both, each with its stock, rather than one of them guessed.
 */
function ProductoEscaneado({
  productos,
  existencias,
  onSeguir,
}: {
  productos: readonly Producto[];
  existencias: readonly Existencia[];
  onSeguir: () => void;
}) {
  const t = useT();
  const nombre = useNombreProducto();
  const raiz = useRef<HTMLDivElement>(null);
  // Not scrolled to: with two products the sheet is taller than the screen, and starts at its top.
  useEffect(() => raiz.current?.focus({ preventScroll: true }), []);
  return (
    <div ref={raiz} tabIndex={-1} className="flex flex-col gap-5 outline-none">
      {productos.length > 1 && (
        <div className="flex items-start gap-3">
          <Chip icono="aviso" tinte="ambar" />
          <p className="min-w-0 self-center font-medium">{t('escaner.varios', { n: productos.length })}</p>
        </div>
      )}
      {productos.map((p, i) => {
        const lugares = existenciaPorLugar(existencias, p.id);
        const total = lugares.reduce((a, l) => a + l.cantidad, 0) as Milli;
        const enSuUnidad = (cantidad: Milli) => `${formatQuantity(cantidad)} ${etiquetaUnidad(p.unidadBase, p.unidadBase)}`;
        /** The answer the scan was for, said before the places it adds up from. */
        const resumen =
          total > 0
            ? { tono: 'text-emerald-300', icono: 'listo' as const, texto: t('inv.hay', { cantidad: enSuUnidad(total) }) }
            : total < 0
              ? { tono: 'text-rose-300', icono: 'aviso' as const, texto: t('inv.hayNegativo', { cantidad: enSuUnidad(total) }) }
              : { tono: 'text-amber-200', icono: 'aviso' as const, texto: t('inv.noHay') };
        const tonoDe = (cantidad: Milli) => (cantidad < 0 ? 'text-rose-400' : cantidad === 0 ? 'text-slate-500' : '');
        // Two products, a line between them: each one's «Ver el producto» stays with its own stock.
        return (
          <section
            key={p.id}
            className={`flex flex-col gap-3 ${i > 0 ? 'border-t border-white/5 pt-5' : ''}`}
            data-escaneado={p.id}
          >
            <div className="flex items-center gap-3">
              <Miniatura producto={p} tamano="lg" />
              <div className="min-w-0 flex-1">
                <p className="font-medium break-words">{nombre(p)}</p>
                <p className={`mt-1 flex items-start gap-1.5 text-sm font-medium ${resumen.tono}`}>
                  <Ico icono={resumen.icono} className="mt-0.5 size-4 shrink-0" />
                  <span className="min-w-0">{resumen.texto}</span>
                </p>
              </div>
            </div>
            {/* Place by place only where some is outside the shop: otherwise the line above is all of it. */}
            {lugares.length > 1 && (
              <dl className="flex flex-col gap-2 rounded-xl bg-slate-800/60 px-4 py-3">
                {lugares.map((l) => (
                  <div key={l.ubicacion} className="flex items-baseline justify-between gap-3">
                    <dt className="min-w-0 text-sm text-slate-400">{etiquetaLugar(l.ubicacion)}</dt>
                    <dd className={`tabular shrink-0 text-sm font-medium ${tonoDe(l.cantidad)}`}>{enSuUnidad(l.cantidad)}</dd>
                  </div>
                ))}
              </dl>
            )}
            <Link to="/productos/$productoId" params={{ productoId: p.id }} className="btn-secundario">
              <Ico icono="productos" />
              {t('productos.verProducto')}
            </Link>
          </section>
        );
      })}
      <button type="button" className="btn-primario" onClick={onSeguir}>
        <Ico icono="escanear" />
        {t('escaner.seguir')}
      </button>
    </div>
  );
}

/**
 * Product leaving for a reason that is not a sale.
 *
 * Without somewhere to record this, the goods are gone from the shelf and still on the books
 * forever — and inventory drifts away from the ledger with no way back.
 */
function SalidaSinVenta({
  producto,
  ubicacion,
  onCerrar,
}: {
  producto: Producto;
  ubicacion: string;
  onCerrar: () => void;
}) {
  const t = useT();
  const [cantidad, setCantidad] = useState('');
  const [motivo, setMotivo] = useState<(typeof MOTIVOS)[number]['valor']>('merma');
  const [nota, setNota] = useState('');
  /** Pressed once: a second press while it was saved booked the loss twice (2026-10-13). */
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const qty = parseQuantity(cantidad);
  const valido = qty !== null && qty > 0;

  async function registrar() {
    if (!valido || ocupado) return;
    setOcupado(true);
    setError(null);
    try {
      const { rechazo } = await emitirYVer('inventario.salida', {
        salidaId: newId(),
        ubicacion,
        fecha: hoy(),
        productoId: producto.id,
        cantidadBase: qty!,
        motivo,
        ...(nota.trim() ? { nota: nota.trim() } : {}),
      });
      if (rechazo) {
        setError(rechazo);
        return;
      }
      onCerrar();
    } catch (e) {
      setError(e instanceof Error ? e.message : t('entrega.noSeGuardo'));
    } finally {
      setOcupado(false);
    }
  }

  return (
    <Modal
      titulo={t('inv.sacarProducto', { producto: nombreProducto(producto) })}
      onCerrar={onCerrar}
      pie={
        <button
          type="button"
          className="btn-primario w-full"
          disabled={!valido || ocupado}
          onClick={() => void registrar()}
        >
          <Ico icono="merma" />
          {t('inv.registrarSalida')}
        </button>
      }
    >
      <div className="flex flex-col gap-4">
        <Campo
          etiqueta={t('inv.cantidadEn', {
            unidad: etiquetaUnidad(producto.unidadBase, producto.unidadBase),
          })}
          inputMode="decimal"
          className="tabular text-lg"
          value={cantidad}
          onChange={(e) => setCantidad(e.target.value)}
          autoFocus
        />
        <Selector
          etiqueta={t('comun.motivo')}
          value={motivo}
          onChange={(e) => setMotivo(e.target.value as typeof motivo)}
          ayuda={t('inv.ayudaMotivo')}
        >
          {MOTIVOS.map((mo) => (
            <option key={mo.valor} value={mo.valor}>
              {t(mo.etiqueta)}
            </option>
          ))}
        </Selector>
        <Campo
          maxLength={300}
          etiqueta={t('comun.nota')}
          value={nota}
          onChange={(e) => setNota(e.target.value)}
          autoComplete="off"
        />
        {error && (
          <p role="alert" className="text-sm text-rose-400">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}

/**
 * A product below zero, counted from its row (the owner, 2026-10-30: «can we instead have a button
 * that let's you recount the item to zero? instead of just write-off?»). «Sacar» takes it further
 * below zero; the warning over the list says a count fixes it, and this is that count, of this
 * product alone, as «Contar» records it: at 0, or at what is typed when some is left. What was sold
 * past the books comes in over, as at any count.
 *
 * `sistema` is what the books say now, live. The count applies the difference (`Conteo`), so they
 * are noted when the number is written, and a 0 left as it was is written when it is recorded.
 */
function ContarEnCero({
  producto,
  ubicacion,
  sistema,
  onCerrar,
}: {
  producto: Producto;
  ubicacion: string;
  sistema: Milli;
  onCerrar: () => void;
}) {
  const t = useT();
  const [cantidad, setCantidad] = useState('0');
  const [anotado, setAnotado] = useState<{ sistema: Milli; contadoEn: string } | null>(null);
  /** Pressed once, as «Sacar»'s sheet is. */
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const unidad = etiquetaUnidad(producto.unidadBase, producto.unidadBase);
  const qty = parseQuantity(cantidad);
  const valido = qty !== null && qty >= 0;

  async function registrar() {
    if (!valido || ocupado) return;
    setOcupado(true);
    setError(null);
    const { sistema: libros, contadoEn } = anotado ?? { sistema, contadoEn: new Date().toISOString() };
    try {
      const { rechazo } = await emitirYVer('inventario.conteo', {
        conteoId: newId(),
        ubicacion,
        fecha: hoy(),
        lineas: [{ productoId: producto.id, cantidadContada: qty!, cantidadSistema: libros, contadoEn }],
      });
      if (rechazo) {
        setError(rechazo);
        return;
      }
      onCerrar();
    } catch (e) {
      setError(e instanceof Error ? e.message : t('entrega.noSeGuardo'));
    } finally {
      setOcupado(false);
    }
  }

  return (
    <Modal
      titulo={t('inv.contarProducto', { producto: nombreProducto(producto) })}
      onCerrar={onCerrar}
      pie={
        <button
          type="button"
          className="btn-primario w-full"
          disabled={!valido || ocupado}
          onClick={() => void registrar()}
        >
          <Ico icono="contar" />
          {t(ocupado ? 'comun.guardando' : 'conteo.registrar')}
        </button>
      }
    >
      <div className="flex flex-col gap-4">
        <p className="text-sm text-slate-400">
          {t('inv.aCeroAyuda', { cantidad: `${formatQuantity(sistema)} ${unidad}` })}
        </p>
        <Campo
          etiqueta={t('inv.cantidadEn', { unidad })}
          inputMode="decimal"
          className="tabular text-lg"
          value={cantidad}
          onChange={(e) => {
            setCantidad(e.target.value);
            setAnotado({ sistema, contadoEn: new Date().toISOString() });
          }}
          // Typing replaces the 0 rather than following it.
          onFocus={(e) => e.currentTarget.select()}
          error={cantidad.trim() !== '' && !valido ? t('conteo.malas', { lista: nombreProducto(producto) }) : null}
          autoComplete="off"
        />
        {error && (
          <p role="alert" className="text-sm text-rose-400">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}
